import React from 'react';
import clsx from 'clsx';
import {
  Breadcrumbs,
  BreadcrumbParam,
} from 'js/components/Breadcrumbs/Breadcrumbs';
import gridStyles from 'src/assets/style/grid.module.css';
import styles from './Hero.module.css';

interface Props {
  children?: React.ReactNode;
  title: string;
  description: string;
  breadcrumbParams?: {
    paths?: BreadcrumbParam[];
    customColor?: string;
    customHoverColor?: string;
  };
  textColor: string;
  bgColorFrom: string;
  bgColorTo: string;
  bgImageTablet: string;
  bgImageDesktop: string;
  simple: boolean;
}

export function Hero(props: Props): React.ReactElement {
  const {
    title,
    description,
    breadcrumbParams = {},
    textColor,
    bgColorFrom,
    bgColorTo,
    bgImageTablet,
    bgImageDesktop,
    simple,
    children,
  } = props;

  const heroClasses = clsx(styles.hero, { [styles.responsive]: !simple });
  const headerClasses = clsx(
    styles.header,
    gridStyles['col-xs-12'],
    gridStyles['col-sm-6'],
    gridStyles['col-md-7'],
    gridStyles['col-lg-8']
  );

  function backgroundGradient(): string {
    const from = bgColorFrom || bgColorTo;
    const to = bgColorTo || bgColorFrom;

    return from && to
      ? `
        .${styles.hero} {
          background-image: linear-gradient(to bottom, ${from}, ${to})
        }
      `
      : '';
  }

  function backgroundImageStyle(minWidth: number, imageUrl: string): string {
    return !simple && imageUrl
      ? `
    @media (min-width: ${minWidth}px) {
      .${styles.hero} {
        background-image: url(${imageUrl}), linear-gradient(to bottom, ${bgColorFrom}, ${bgColorTo});
      }
    }
  `
      : '';
  }

  const tabletBackgroundImageStyle = backgroundImageStyle(520, bgImageTablet);
  const desktopBackgroundImageStyle = backgroundImageStyle(992, bgImageDesktop);
  const childrenContent = children ? (
    <div className={clsx(styles.wrapper, gridStyles.row)}>{children}</div>
  ) : null;

  return (
    <div className={heroClasses} style={{ color: textColor }}>
      <style>{backgroundGradient()}</style>
      <style>{tabletBackgroundImageStyle}</style>
      <style>{desktopBackgroundImageStyle}</style>

      <div className={clsx(styles.wrapper, gridStyles.row)}>
        <div className={headerClasses}>
          <Breadcrumbs
            breadcrumbsParams={breadcrumbParams.paths || []}
            customColor={breadcrumbParams.customColor}
            customHoverColor={breadcrumbParams.customHoverColor}
            showIcon
          />
          <h1 className={styles.title}>{title}</h1>
          <div className={styles.description}>{description}</div>
        </div>
      </div>
      {childrenContent}
    </div>
  );
}
